import type {
  AdminOperatorMessage,
  AdminOperatorRun,
  AdminOperatorRunDetail,
  AdminOperatorRunOutcome,
  AdminOperatorRunStep
} from "@domain/ai_operator"

export const OPERATOR_PERIODS = ["1h", "24h", "7d", "30d", "all"] as const
export type OperatorPeriod = (typeof OPERATOR_PERIODS)[number]

const PERIOD_MS: Record<Exclude<OperatorPeriod, "all">, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000
}

/** "all" lists everything; the health strip still needs a window, so it reads the last 30 days. */
export function periodFrom(
  period: OperatorPeriod,
  now = Date.now(),
  forHealth = false
): string | undefined {
  if (period !== "all") {
    return new Date(now - PERIOD_MS[period]).toISOString()
  }
  if (!forHealth) return undefined
  return new Date(now - PERIOD_MS["30d"]).toISOString()
}

export const OPERATOR_SHOW: ReadonlyArray<"FAILED" | AdminOperatorRunOutcome> =
  ["FAILED", "ANSWERED", "UNANSWERED", "CLARIFIED", "PROPOSED", "ACTED"]
export type OperatorShow = (typeof OPERATOR_SHOW)[number] | ""

/** One filter for the list: "FAILED" is the run's status, the rest are its outcome. */
export function showFilter(show: OperatorShow): {
  status?: "FAILED"
  outcome?: AdminOperatorRunOutcome
} {
  if (!show) return {}
  return show === "FAILED" ? { status: show } : { outcome: show }
}

export type RunTone = "error" | "warning" | "info" | "success"

export function runTone(
  run: Pick<AdminOperatorRun, "status" | "outcome">
): RunTone {
  if (run.status === "FAILED") return "error"
  if (run.status === "RUNNING") return "info"
  return run.outcome === "UNANSWERED" ? "warning" : "success"
}

export function formatMs(ms?: number | null): string {
  if (ms == null) return "—"
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(1)} sn`
}

export function formatUsd(value?: number | null): string {
  if (!value) return "$0"
  return value < 0.01 ? `$${value.toFixed(5)}` : `$${value.toFixed(4)}`
}

/** Where the run went wrong: the first failed step, else the run's own error stage. */
export function firstFailure(
  detail: AdminOperatorRunDetail
): { step?: AdminOperatorRunStep; stage?: string; message?: string } | null {
  const step = detail.steps.find((item) => !item.ok)
  if (step) return { step, stage: step.name, message: step.error }
  if (detail.run.error) {
    return { stage: detail.run.error.stage, message: detail.run.error.message }
  }
  return null
}

/** Left offset and width of each step on the run's waterfall, in percent of the run. */
export function waterfall(
  steps: AdminOperatorRunStep[],
  totalMs?: number | null
): { left: number; width: number }[] {
  const end = Math.max(
    totalMs ?? 0,
    ...steps.map((step) => step.atMs + step.durationMs),
    1
  )
  return steps.map((step) => ({
    left: (step.atMs / end) * 100,
    width: Math.max((step.durationMs / end) * 100, 0.6)
  }))
}

export function blockSummary(
  block: AdminOperatorMessage["blocks"][number]
): string {
  for (const key of ["t", "text", "title", "question", "summary", "label"]) {
    const value = block[key]
    if (typeof value === "string" && value.trim()) return value
  }
  if (typeof block.tool === "string") return block.tool
  return ""
}

/** A cut value is no longer valid JSON, so it is shown as text rather than flagged by the JSON viewer. */
export function formatBlock(value: unknown): {
  text: string
  language: "json" | "plaintext"
} {
  if (typeof value !== "string") {
    return { text: JSON.stringify(value, null, 2) ?? "", language: "json" }
  }
  try {
    const parsed: unknown = JSON.parse(value)
    if (parsed !== null && typeof parsed === "object") {
      return { text: JSON.stringify(parsed, null, 2), language: "json" }
    }
  } catch {
    return { text: value, language: "plaintext" }
  }
  return { text: value, language: "plaintext" }
}
